/**
 * useProvenanceChain — Shared hook for fetching a full provenance chain.
 *
 * - Uses SDK authenticated fetch (UI-API-01)
 * - Implements ETag conditional GET (UI-PERF-03)
 * - Cache key includes tenantId to prevent stale data on tenant switch
 * - SWR-style 30s cache window
 * - Handles 404 gracefully
 */

import type { ActionProvenanceChain } from '../components/provenance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;
const { usePlugin } = (globalThis as any).PluginSDK || {};

interface CacheEntry {
  chain: ActionProvenanceChain;
  etag: string;
  timestamp: number;
}

const CACHE_TTL_MS = 30_000;
const cache = new Map<string, CacheEntry>();

export interface UseProvenanceChainResult {
  chain: ActionProvenanceChain | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string;
  refetch: () => void;
}

export function useProvenanceChain(
  actionId: string | null,
): UseProvenanceChainResult {
  const plugin = usePlugin?.() ?? null;
  const sdkApi = plugin?.sdk?.api ?? null;
  const tenantId: string = plugin?.context?.tenantId ?? 'default';

  const [chain, setChain] = useState(null);
  const [status, setStatus] = useState(
    actionId ? 'loading' : 'idle',
  );
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  const cacheKey = actionId ? `${tenantId}:${actionId}` : '';

  const fetchChain = useCallback(async () => {
    if (!actionId) {
      setStatus('idle');
      return;
    }

    const cached = cache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      setChain(cached.chain);
      setStatus('success');
      return;
    }

    setStatus('loading');
    setError('');

    const resolveFetch = (): typeof globalThis.fetch => {
      if (sdkApi?.fetch) return sdkApi.fetch;
      return globalThis.fetch.bind(globalThis);
    };

    const doFetch = resolveFetch();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (cached?.etag) {
      headers['If-None-Match'] = cached.etag;
    }

    try {
      const response = await doFetch(
        `/api/v1/analytics/actions/${encodeURIComponent(actionId)}/chain`,
        { method: 'GET', credentials: 'include' as RequestCredentials, headers },
      );

      if (!mountedRef.current) return;

      if (response.status === 304 && cached) {
        cache.set(cacheKey, { ...cached, timestamp: now });
        setChain(cached.chain);
        setStatus('success');
        return;
      }

      if (response.status === 404) {
        setChain(null);
        setStatus('error');
        setError('No receipts found for this action. The receipts may not have been ingested yet.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ActionProvenanceChain = await response.json();
      const etag = response.headers.get('ETag') ?? '';

      cache.set(cacheKey, { chain: data, etag, timestamp: now });
      setChain(data);
      setStatus('success');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setStatus('error');
      setError(err?.message ?? 'Failed to load provenance chain');
    }
  }, [actionId, cacheKey, sdkApi]);

  useEffect(() => {
    mountedRef.current = true;
    fetchChain();
    return () => { mountedRef.current = false; };
  }, [fetchChain]);

  return { chain, status, error, refetch: fetchChain };
}

export default useProvenanceChain;
